import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import { Card, Box, CardMedia, CardContent, Typography, CircularProgress, CardActionArea, Stack, SvgIcon } from '@mui/material';
import { css } from '@emotion/css';

import { StakingActions } from 'store/reducers/staking';

import { getLink } from 'helpers/routes';
import { blockChainCurrency } from 'helpers/blockchain';
import { checkAuctionInProgress } from 'helpers/token';

import {Token, TokenGame} from 'entities/Token';

import * as routePaths from 'constants/routes';

import {BinanceIcon, EthereumIcon, PolygonIcon, HammerIcon, WhitebitIcon2} from 'icons';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { AuctionTimer } from 'shared/components/Timer';
import Player from 'shared/components/Player/Player';
import Avatar from 'shared/components/Avatar';

import { videoTypes } from 'constants/filters';

import Checkbox from '@mui/material/Checkbox';
import Exclusive from './help/Exclusive';

import { useStyles } from './styles';
import Power from './help/Power';
import {IGame} from "../../../../store/reducers/games/types";

export interface NFTCardProps {
  exclusive: boolean;
  data?: Token | null;
  gameData?: IGame | null;
  disableView?: boolean | null;
  title?: string;
  previewImg?: string;
  collection?: boolean | null;
  subscribe?: boolean;
  fileType?: string;
  power?: string;
  isSecondary?: boolean;
  price?: number | string | null;
  amountOfCopiesList?: number | string;
  blockchain?: string;
  onClick?(): void;
  isCheckbox?: boolean;
  selectAll?: any;
  showPower?: boolean;
  username: string;
  addClass?: string;
  ownerTitle?: string;
  instantPurchase?: boolean;
  showOwner?: boolean;
  currencyAssetsSymbol?: string;
}

const BlockchainIcon = ({ blockchain }: Pick<NFTCardProps, 'blockchain'>) => {
  switch (blockchain) {
    case 'eth':
      return <EthereumIcon />;
    case 'bsc':
      return <BinanceIcon />;
    case 'polygon':
      return <PolygonIcon />;
    case 'whitebit':
      return <WhitebitIcon2 />;
    default:
      return null;
  }
};

const NFTCard = (props: NFTCardProps) => {
  const {
    fileType,
    previewImg,
    title,
    data,
    gameData,
    disableView,
    collection,
    subscribe,
    exclusive,
    price,
    isSecondary,
    onClick,
    amountOfCopiesList,
    blockchain,
    power,
    isCheckbox,
    selectAll,
    showPower,
    username,
    addClass,
    ownerTitle,
    instantPurchase,
    showOwner,
    currencyAssetsSymbol,
  } = props;

  const [checked, setChecked] = useState(false);
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);

  const { classes, cx } = useStyles();

  const dispatch = useDispatch();

  const {
    account: { account },
  } = useSelector((state) => state);

  const isCelebrity = account?.role === 'celebrity';
  // @ts-ignore
  const type = previewImg?.split('.')[previewImg?.split('.').length - 1];
  const isVideo = (fileType && videoTypes.includes(fileType)) || (type && videoTypes.includes(type));
  const nickName = account?.nickName;
  const owner = data?.owner?.nickName;
  const isMyToken = nickName === owner;
  const thumbnailImage = data?.imageThumbnail ? data?.imageThumbnail : previewImg;
  const nonplatform = data?.status === 'active';
  const auctionInProgress = data ? checkAuctionInProgress(data) : false;
  const creator = data?.creator?.nickName || username;

  useEffect(() => {
    selectAll ? setChecked(true) : setChecked(false);
  }, [selectAll]);

  const renderPrice = (value: number) => {
    if (+value <= 1) return +(+value).toFixed(5);
    if (+value <= 1000) return `~${(+value).toFixed(2)}`;

    return Math.floor(+value);
  };

  const tokenPrice = collection ? (price && price > 0 ? price : null) : instantPurchase ? data?.buyoutPricePerToken || price : price && price >= 0.00001 ? +price : null;
  const tokenPriceString = tokenPrice ? `${renderPrice(Number(tokenPrice))} ${currencyAssetsSymbol ?? blockChainCurrency(blockchain)}` : '';

  const handleCheck = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();

    setChecked(!checked);

    if (!checked) {
      dispatch(StakingActions.checkedTokens.checkTokenRequest({ tokenId: data?.staking.tokenId, tokenAddress: data?.staking.tokenAddress, nonplatform }));
    }
    if (checked) {
      dispatch(StakingActions.checkedTokens.unCheckTokenRequest({ tokenId: data?.staking.tokenId }));
    }
  };

  const CardActionWrapper = (params: any) => {
    const { children } = params;

    if (disableView) {
      return <CardActionArea>{children}</CardActionArea>;
    }

    if (isCheckbox) {
      return <CardActionArea onClick={handleCheck}>{children}</CardActionArea>;
    }

    if (exclusive && !isMyToken && !isCelebrity && !isSecondary && onClick && onClick.name === 'openAccess') {
      return <CardActionArea onClick={onClick}>{children}</CardActionArea>;
    }

    return (
      <CardActionArea component={RouterLink} to={getLink(routePaths.ROUTE_COLLECTIBLES_DETAILS, { id: data?.nftId, tokenSource: blockchain })}>
        {children}
      </CardActionArea>
    );
  };

  const OwnerBox = ({ avatar, label, name }: { avatar?: string | null; label?: string; name?: string }) => (
    <Box>
      <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1} sx={{ minWidth: 0, overflow: 'hidden' }}>
        <Avatar src={avatar} alt={name} sx={{ width: 24, height: 24 }} />
        <Stack direction="column" justifyContent="center" alignItems="flex-start" spacing={{ xs: 0.5, md: 0 }} sx={{ overflow: 'hidden' }}>
          <Typography color="secondary" variant="h5" className={classes.title}>
            {label}
          </Typography>
          <Typography variant="h5" color="black" className={classes.creatorName} title={creator} sx={{ width: '100%' }}>
            {name}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );

  return (
    <>
      <Card className={cx(classes.root, 'NFTCard-Root', { disableView }, addClass)} elevation={0}>
        <CardActionWrapper>
          {exclusive && <Exclusive />}
          <Box className={cx(classes.mediaWrap, { [classes.blur]: !subscribe && exclusive && !isSecondary }, addClass)}>
            {showPower && <Power power={+power!} exclusive={exclusive} />}
            {isCheckbox && (
              <Checkbox checked={checked} onClick={handleCheck} className={classes.checkbox} icon={<RadioButtonUncheckedIcon />} checkedIcon={<CheckCircleIcon />} />
            )}
            {isVideo ? (
              <Player
                url={(data && data?.previewUrl) || previewImg}
                className={classes.video}
                height={250}
                // loop
                onReady={() => {
                  setImageLoaded(true);
                }}
                onError={() => {
                  setImageLoaded(true);
                }}
                ligh={(data && data?.imageThumbnail) || false}
              />
            ) : (
              <CardMedia
                component="img"
                onLoad={() => {
                  setImageLoaded(true);
                }}
                onError={() => {
                  setImageLoaded(true);
                }}
                src={thumbnailImage}
                className={classes.img}
              />
            )}
            {imageLoaded || <CircularProgress color="primary" className={classes.previewLoader} />}
          </Box>
          <div className={css({ flexGrow: 1 })} />
          <CardContent>
            <Box>
              {auctionInProgress && (
                <Box mb={1} className={css({ textAlign: 'center' })}>
                  <AuctionTimer data={data} reload={false} tiny />
                </Box>
              )}
              <Box display="flex" mb={1} alignItems="center">
                <BlockchainIcon blockchain={blockchain?.toLowerCase()} />
                <Box flexGrow={1} mx={1} sx={{ overflow: 'hidden' }}>
                  <Typography variant="h4" className={classes.contentTitle} title={title || 'NFT Card'}>
                    {title}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h4" color="secondary" className={classes.amountOfCopies} sx={{ whiteSpace: 'nowrap' }}>
                    {collection ? amountOfCopiesList : '1/1'}
                  </Typography>
                </Box>
              </Box>

              <OwnerBox avatar={showOwner ? data?.owner?.avatar : data?.creator?.avatar} label={ownerTitle} name={showOwner ? owner : creator} />

              <Box display="flex" justifyContent="center" className={classes.priceBox}>
                {tokenPrice && (
                  <Typography variant="h4" className={cx({ collection })}>
                    {instantPurchase && <SvgIcon component={HammerIcon} inheritViewBox sx={{ marginRight: 1 }} />}
                    {collection
                      ? data?.totalOnSaleCollectibles && data?.totalOnSaleCollectibles > 0 || gameData?.totalTokenAmount && +gameData?.totalTokenAmount > 0
                        ? parse(tokenPriceString)
                        : 'No listings'
                      : parse(tokenPriceString)}
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </CardActionWrapper>
      </Card>
    </>
  );
};

NFTCard.propTypes = {
  exclusive: PropTypes.bool,
  username: PropTypes.string,
};

NFTCard.defaultProps = {
  price: null,
  fileType: 'img',
  data: null,
  amountOfCopiesList: 0,
  disableView: false,
  collection: false,
  subscribe: true,
  exclusive: false,
  title: 'Test title',
  previewImg: 'https://via.placeholder.com/282x250.png?text=Processing+Image',
  power: '0',
  username: '',
  ownerTitle: 'Creator',
  instantPurchase: false,
  showOwner: false,
};

export default NFTCard;
